/**
 * Deterministic Doc -> HTML serialization for non-browser boundaries (REST,
 * jobs, and tests).
 *
 * This module owns markup structure only — which element each layer type
 * becomes, and which attributes it carries. The CSS is not defined here: every
 * declaration comes from `layer-css.ts`, which the editor's React renderer
 * consumes through the same functions. That's what keeps "what the editor
 * shows" and "what we ship" from drifting; see that module's header.
 */
import {
  type CssMap,
  cropImageCss,
  cssToStyleString,
  docRootCss,
  layerCss,
} from "./layer-css";
import type { Doc, ImageLayer, Layer, SvgLayer, TextLayer } from "./model";
import { isFlexGroup } from "./model";
import { HT_ID_ATTR, HT_MOTION_ATTR } from "./ids";

const HT_LOCKED_ATTR = "data-ht-locked";
const HT_LABEL_ATTR = "data-label";
const HT_CROP_ATTR = "data-ht-crop";
const DATA_SLOT_ATTR = "data-slot";
const DATA_LAYER_TYPE_ATTR = "data-layer-type";

export interface SerializeDocOptions {
  /**
   * Emit each layer's `id` as a `data-ht-id` attribute so it survives the
   * serialize → parse round-trip (`parse.ts` reads `data-ht-id` back as the
   * layer id). Off by default — static-ad markup doesn't need ids and the
   * save path strips them — but load-bearing for video HTML overlays, whose
   * animation bindings key off layer ids sidecar to the markup.
   */
  layerIds?: boolean;
}

export function serializeDoc(
  doc: Doc,
  options: SerializeDocOptions = {},
): string {
  const inner = doc.layers
    .map((layer) => serializeLayer(layer, false, options))
    .join("");
  return `<div style="${style(docRootCss(doc))}">${inner}</div>`;
}

function serializeLayer(
  layer: Layer,
  inFlow: boolean,
  options: SerializeDocOptions,
): string {
  switch (layer.type) {
    case "rect":
      return `<div${attrs(layer, options)} style="${style(layerCss(layer, inFlow))}"></div>`;
    case "text":
      return serializeText(layer, inFlow, options);
    case "image":
      return serializeImage(layer, inFlow, options);
    case "svg":
      return serializeSvg(layer, inFlow, options);
    case "group":
      return serializeGroup(layer, inFlow, options);
  }
}

function serializeText(
  layer: TextLayer,
  inFlow: boolean,
  options: SerializeDocOptions,
): string {
  return `<div${attrs(layer, options)} style="${style(layerCss(layer, inFlow))}">${layer.content}</div>`;
}

function serializeImage(
  layer: ImageLayer,
  inFlow: boolean,
  options: SerializeDocOptions,
): string {
  // Persist the portable `ad:` form when the layer carries one — the resolved
  // URL is workspace- and time-scoped and must never reach saved markup.
  const src = layer.adRef ?? layer.src;
  if (layer.crop) {
    return (
      `<div${attrs(layer, options)} ${HT_CROP_ATTR}="1" style="${style(layerCss(layer, inFlow))}">` +
      `<img src="${escapeAttr(src)}" style="${style(cropImageCss(layer.crop, layer.objectFit))}">` +
      "</div>"
    );
  }
  return `<img${attrs(layer, options)} src="${escapeAttr(src)}" style="${style(layerCss(layer, inFlow))}">`;
}

function serializeSvg(
  layer: SvgLayer,
  inFlow: boolean,
  options: SerializeDocOptions,
): string {
  const viewBox = layer.viewBox
    ? ` viewBox="${escapeAttr(layer.viewBox)}"`
    : "";
  return `<svg${attrs(layer, options)} style="${style(layerCss(layer, inFlow))}"${viewBox}>${layer.innerMarkup}</svg>`;
}

function serializeGroup(
  layer: Layer & { type: "group" },
  inFlow: boolean,
  options: SerializeDocOptions,
): string {
  const inner = layer.children
    .map((child) => serializeLayer(child, isFlexGroup(layer), options))
    .join("");
  return `<div${attrs(layer, options)} style="${style(layerCss(layer, inFlow))}">${inner}</div>`;
}

/**
 * A style attribute's value, escaped and terminated. The trailing semicolon is
 * historical but load-bearing for byte-stability of already-saved markup.
 */
function style(css: CssMap): string {
  return `${escapeAttr(cssToStyleString(css))};`;
}

function attrs(layer: Layer, options: SerializeDocOptions): string {
  const parts: string[] = [];
  if (options.layerIds && layer.id) {
    parts.push(`${HT_ID_ATTR}="${escapeAttr(layer.id)}"`);
  }
  if (layer.slotId)
    parts.push(`${DATA_SLOT_ATTR}="${escapeAttr(layer.slotId)}"`);
  if (layer.layerType)
    parts.push(`${DATA_LAYER_TYPE_ATTR}="${escapeAttr(layer.layerType)}"`);
  if (layer.locked) parts.push(`${HT_LOCKED_ATTR}="true"`);
  if (layer.label) parts.push(`${HT_LABEL_ATTR}="${escapeAttr(layer.label)}"`);
  // Emitted only when there is motion, so static ads (which never carry it)
  // keep their exact current byte shape.
  if (layer.motion && layer.motion.blocks.length > 0)
    parts.push(
      `${HT_MOTION_ATTR}="${escapeAttr(JSON.stringify(layer.motion))}"`,
    );
  return parts.length > 0 ? ` ${parts.join(" ")}` : "";
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}
